/**
 * Single source of truth for offer and invoice totals.
 *
 * The same formula used to live in four places (manual save, auto save, the
 * public sync inside the editor and the resync after a catalog change). Only
 * some of them honoured the per-offer `serviceOverrides`, which meant a public
 * offer could show adjusted line items next to catalog based totals.
 */

/** Price of a service inside one specific offer. */
export const getEffectiveServicePrice = (offerData, service) => {
  const override = offerData?.serviceOverrides?.[service?.id];
  return override?.price !== undefined ? override.price : service?.price || 0;
};

/**
 * @param {object} offerData offer document (needs serviceOverrides, globalDiscount, tax fields)
 * @param {Array} servicesData services of this offer, resolved from the catalog
 */
export const calculateOfferPricing = (offerData, servicesData) => {
  const services = servicesData || [];

  const originalTotal = services.reduce(
    (sum, service) => sum + getEffectiveServicePrice(offerData, service),
    0
  );

  const servicesDiscountedTotal = services.reduce((sum, service) => {
    const price = getEffectiveServicePrice(offerData, service);
    return sum + price * (1 - (service.discountPercent || 0) / 100);
  }, 0);

  const discountedTotal =
    servicesDiscountedTotal * (1 - (offerData?.globalDiscount || 0) / 100);

  const taxAmount =
    offerData?.includeTax && !offerData?.reverseCharge
      ? discountedTotal * ((offerData.taxRate || 0) / 100)
      : 0;

  return {
    originalTotal,
    servicesDiscountedTotal,
    discountedTotal,
    taxAmount,
    finalTotal: discountedTotal + taxAmount,
  };
};
